import { Injectable, Type } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';

/*
* type StackingStrategy
* allow - will allow multiple instances of the same dialog to display at once
* replace - will close existing dialogs of the same type and open a new one
* ignore - will keep a dialog of the same type open
*/
export type StackingStrategy = 'allow' | 'replace' | 'ignore'

export interface DialogConfig<T> extends MatDialogConfig<T> {
  multi: StackingStrategy;
}

export interface OpenDialog<T> {
  component: Type<T>;
  ref: MatDialogRef<T>;
}

/**
 * @deprecated Legacy Vision: Please upgrade to the newer CdkDialogService
 */
@Injectable()
export class FwLegacyDialogService {
  readonly dialogs: OpenDialog<unknown>[] = [];

  private config = new Map<Type<unknown>, DialogConfig<unknown>>();

  constructor(
    private matDialog: MatDialog,
  ) {
  }

  closeAll(component?: Type<unknown>): void {
    if (component) {
      this.dialogs
        .filter((el) => el.component === component)
        .forEach((el) => el.ref.close());
    } else {
      this.matDialog.closeAll();
    }
  }

  open<T, D, R>(component: Type<T>, config?: MatDialogConfig<D>): MatDialogRef<T, R> {
    const index = this.dialogs.findIndex(el => el.component === component);
    let ref: MatDialogRef<T, R> = null;
    const baseConfig = {
      multi: 'allow',
      ...this.config.get(component),
    };

    switch (baseConfig.multi) {
      case 'ignore':
        // if an existing dialog of the same type doesn't exist, open one
        if (index === -1) {
          ref = this.matDialog.open(component, { ...baseConfig, ...config });
          this.dialogs.push({ component, ref });
        }
        break;
      case 'replace':
        // if an existing dialog of the same type exists, replace it.
        if (index > -1) {
          this.dialogs[index].ref.close();
          ref = this.matDialog.open(component, { ...baseConfig, ...config });
          this.dialogs.splice(index, 1, { component, ref });
        } else {
          ref = this.matDialog.open(component, { ...baseConfig, ...config });
          this.dialogs.push({ component, ref });
        }
        break;
      default:
        ref = this.matDialog.open(component, { ...baseConfig, ...config });
        this.dialogs.push({ component, ref });
        break;
    }

    if (ref) {
      ref.afterClosed().subscribe(() => {
        // clean up open dialog reference
        const index = this.dialogs.findIndex(el => el.ref === ref);
        if (index >= 0) {
          this.dialogs.splice(index, 1);
        }
      });
    }

    return ref;
  }

  registerDialog(component: Type<unknown>, config: DialogConfig<unknown>): void {
    this.config.set(component, config);
  }
}
